import { useEffect, useRef, useState } from "react";

function useCoarsePointer() {
  const [coarse, setCoarse] = useState(() => {
    if (typeof window === "undefined") return false;
    return (
      (typeof navigator !== "undefined" && navigator.maxTouchPoints > 0) ||
      window.matchMedia?.("(pointer: coarse)")?.matches
    );
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(pointer: coarse)");
    const sync = () => setCoarse(mq.matches || (navigator.maxTouchPoints || 0) > 0);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  return coarse;
}

/**
 * Touch / coarse-pointer drag from tray chips to drop zones (data-pose-id).
 */
export default function useYogaTouchDrag({ onDrop, enabled = true }) {
  const isCoarse = useCoarsePointer();
  const allowHtml5Drag = !isCoarse;
  const pickRef = useRef(null);
  const dragIdRef = useRef({ mode: "", id: null });
  const lastClientRef = useRef({ x: 0, y: 0, valid: false });
  const onDropRef = useRef(onDrop);
  const [ghost, setGhost] = useState({ visible: false, x: 0, y: 0, label: "" });
  const [hoverPoseId, setHoverPoseId] = useState(null);

  useEffect(() => {
    onDropRef.current = onDrop;
  }, [onDrop]);

  useEffect(() => {
    if (!enabled) return undefined;

    const findPoseIdAt = (x, y) => {
      const el = document.elementFromPoint(x, y);
      const zone = el?.closest?.("[data-pose-id]");
      if (!zone) return null;
      const id = zone.getAttribute("data-pose-id");
      return id ? Number.parseInt(id, 10) : null;
    };

    const finish = (clientX, clientY, preferLast = false) => {
      const pick = pickRef.current;
      if (!pick) return;
      let x = clientX;
      let y = clientY;
      const last = lastClientRef.current;
      if (preferLast && last.valid) {
        x = last.x;
        y = last.y;
      } else if (last.valid) {
        const bad = !Number.isFinite(x) || !Number.isFinite(y) || (Math.abs(x) < 1 && Math.abs(y) < 1);
        if (bad) {
          x = last.x;
          y = last.y;
        }
      }
      const poseId = findPoseIdAt(x, y);
      pickRef.current = null;
      dragIdRef.current = { mode: "", id: null };
      lastClientRef.current = { x: 0, y: 0, valid: false };
      setGhost({ visible: false, x: 0, y: 0, label: "" });
      setHoverPoseId(null);
      if (poseId != null && pick) {
        onDropRef.current?.(poseId, pick);
      }
    };

    const onPointerMove = (e) => {
      if (!pickRef.current) return;
      const hasTouch = typeof navigator !== "undefined" && navigator.maxTouchPoints > 0;
      if (e.pointerType === "mouse" && !hasTouch) return;
      if (dragIdRef.current.mode === "pointer" && dragIdRef.current.id != null && e.pointerId !== dragIdRef.current.id) {
        return;
      }
      lastClientRef.current = { x: e.clientX, y: e.clientY, valid: true };
      setGhost({ visible: true, x: e.clientX, y: e.clientY, label: pickRef.current });
      setHoverPoseId(findPoseIdAt(e.clientX, e.clientY));
    };

    const onPointerEnd = (e) => {
      const hasTouch = typeof navigator !== "undefined" && navigator.maxTouchPoints > 0;
      if (e.pointerType === "mouse" && !hasTouch) return;
      if (dragIdRef.current.mode === "pointer" && dragIdRef.current.id != null && e.pointerId !== dragIdRef.current.id) {
        return;
      }
      finish(e.clientX, e.clientY, false);
    };

    const onPointerCancel = (e) => {
      finish(e.clientX, e.clientY, true);
    };

    window.addEventListener("pointermove", onPointerMove, true);
    window.addEventListener("pointerup", onPointerEnd, true);
    window.addEventListener("pointercancel", onPointerCancel, true);
    return () => {
      window.removeEventListener("pointermove", onPointerMove, true);
      window.removeEventListener("pointerup", onPointerEnd, true);
      window.removeEventListener("pointercancel", onPointerCancel, true);
    };
  }, [enabled]);

  const startPointerPick = (e, name) => {
    if (!enabled || allowHtml5Drag || !name) return;
    e.preventDefault();
    pickRef.current = name;
    dragIdRef.current = { mode: "pointer", id: e.pointerId };
    lastClientRef.current = { x: e.clientX, y: e.clientY, valid: true };
    setGhost({ visible: true, x: e.clientX, y: e.clientY, label: name });
  };

  return {
    allowHtml5Drag,
    ghost,
    hoverPoseId,
    startPointerPick,
  };
}
